import { useEffect } from "react";
import { isStandaloneMode } from "./pwaUtils";

const DEFAULT_CHROME = "#7c3aed";
const CLIENT_PAGE_BG = "#f4f5f8";
const ADMIN_PAGE_BG = "#f4f5f8";
const PORTAL_DARK_BG = "#0f1117";

function parseHex(hex) {
  const m = String(hex || "").match(/^#([0-9A-Fa-f]{6})$/);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Относительная яркость 0…1 — для выбора светлого/тёмного статус-бара iOS. */
export function isDarkHexColor(hex) {
  const rgb = parseHex(hex);
  if (!rgb) return true;
  const { r, g, b } = rgb;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum < 0.55;
}

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Цвет зоны статус-бара / «чёлки».
 * @param {string} color — hex
 * @param {object} options
 * @param {boolean} [options.isDark] — тёмный статус-бар (белые иконки)
 * @param {boolean} [options.immersive] — full-bleed верх (splash/баннер): без полоски, контент под чёлкой
 */
export function applyPwaChromeColor(
  color,
  { isDark, immersive = false, themeColor, pageBg } = {},
) {
  if (typeof document === "undefined") return;

  const bg = parseHex(color) ? color : DEFAULT_CHROME;
  const metaColor = parseHex(themeColor) ? themeColor : bg;
  const page = parseHex(pageBg) ? pageBg : bg;
  const dark = isDark ?? isDarkHexColor(bg);
  const root = document.documentElement;
  const standalone = isStandaloneMode();
  const portalApp =
    root.classList.contains("client-app-mode") ||
    root.classList.contains("admin-app-mode") ||
    root.classList.contains("owner-app-mode");

  setMeta("theme-color", metaColor);
  // Всегда translucent в порталах/PWA: под чёлкой — продолжение экрана, не отдельная полоса
  setMeta(
    "apple-mobile-web-app-status-bar-style",
    standalone || portalApp || dark || immersive
      ? "black-translucent"
      : "default",
  );

  root.style.setProperty("--pwa-chrome-bg", bg);
  root.style.setProperty("--pwa-page-bg", page);
  root.classList.add("pwa-chrome-themed");
  // pwa-chrome-dark больше не трогаем для color-scheme (см. App.css + data-portal-theme)
  root.classList.toggle("pwa-chrome-immersive", immersive);
}

export function resetPwaChromeColor() {
  if (typeof document === "undefined") return;
  applyPwaChromeColor(DEFAULT_CHROME, { isDark: true, immersive: false });
}

export function portalPageBg(portal = "admin", themeMode = "light") {
  if (themeMode === "dark") return PORTAL_DARK_BG;
  return portal === "client" ? CLIENT_PAGE_BG : ADMIN_PAGE_BG;
}

/**
 * Единые настройки «чёлки» для PWA: учитывает тёмную тему портала и hero-экраны.
 */
export function resolvePwaChromeConfig({
  portal = "admin",
  themeMode = "light",
  theme = null,
  immersive = false,
} = {}) {
  const isDarkPortal = themeMode === "dark";
  const pageBg = portalPageBg(portal, themeMode);
  const hasCover = Boolean(theme?.coverUrl);
  // Immersive: theme-color ≈ верх экрана (баннер/accent), не чужой slate — иначе «полоска»
  const heroChrome = immersive
    ? (parseHex(theme?.from) && theme.from) ||
      (parseHex(theme?.accent) && theme.accent) ||
      (hasCover ? pageBg : null)
    : null;
  const chromeBg = heroChrome || pageBg;
  const themeColor = heroChrome || pageBg;
  const isDark =
    isDarkPortal || (heroChrome ? isDarkHexColor(heroChrome) : isDarkHexColor(chromeBg));

  return { color: chromeBg, themeColor, isDark, immersive, pageBg };
}

export { CLIENT_PAGE_BG, ADMIN_PAGE_BG, PORTAL_DARK_BG };

/** Синхронизирует «чёлку» при смене страницы / брендинга. */
export function usePwaChrome(
  color,
  { enabled = true, isDark, immersive = false, themeColor, pageBg } = {},
) {
  useEffect(() => {
    if (!enabled || !color) return undefined;
    applyPwaChromeColor(color, { isDark, immersive, themeColor, pageBg });

    const refresh = () => {
      applyPwaChromeColor(color, { isDark, immersive, themeColor, pageBg });
    };
    window.addEventListener("orientationchange", refresh);
    window.visualViewport?.addEventListener("resize", refresh);
    const t = window.setTimeout(refresh, 350);

    return () => {
      window.clearTimeout(t);
      window.removeEventListener("orientationchange", refresh);
      window.visualViewport?.removeEventListener("resize", refresh);
    };
  }, [color, enabled, isDark, immersive, themeColor, pageBg]);
}
